import React from 'react';
import { formatDateStable, formatTimeStable } from '../utils/formatUtils';
import { getStatusColor } from '../utils/styleUtils';
import GroupHeader from '../GroupHeader';
import GroupStatistics from '../GroupStatistics';
import WorklogCard from '../WorklogCard';

const IssueGroupedView = ({ 
  groupedWorkLogs, 
  grouping, 
  onToggleGroupExpansion,
  headerControls 
}) => {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-white/10">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="min-w-0 flex-shrink">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Time Tracking by Issue</h3>
            <p className="text-sm text-gray-600 dark:text-slate-400">
              Grouped by individual issues
            </p>
          </div>
          <div className="min-w-0 flex-shrink">
            {headerControls}
          </div>
        </div>
      </div>
      <div className="divide-y divide-gray-200 dark:divide-white/10">
        {Object.entries(groupedWorkLogs).map(([issueKey, group]) => (
          <div key={issueKey}>
            <GroupHeader
              group={group}
              groupKey={issueKey}
              groupingField={grouping.field}
              isExpanded={grouping.expanded[issueKey]}
              onToggle={() => onToggleGroupExpansion(issueKey)}
            />
            
            {grouping.expanded[issueKey] && (
              <>
                <GroupStatistics group={group} />
                
                <div className="px-3 sm:px-4 py-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-3 text-xs text-gray-600 dark:text-slate-400">
                    <div>
                      <span className="text-gray-500 dark:text-slate-500">Status:</span>
                      <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(group.issue_status)}`}>
                        {group.issue_status}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-slate-500">Assignee:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-slate-100">{group.issue_assignee || 'Unassigned'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-slate-500">Priority:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-slate-100">{group.issue_priority || 'None'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-slate-500">Efficiency:</span>
                      <span className={`ml-2 font-medium ${group.efficiency > 100 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                        {group.efficiency.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  
                  {/* Mobile: Card View */}
                  <div className="md:hidden space-y-3">
                    {group.logs.map((log, index) => (
                      <WorklogCard key={index} log={log} />
                    ))}
                  </div>

                  {/* Desktop: Table View */}
                  <div className="hidden md:block overflow-x-auto -mx-3 sm:mx-0">
                    <div className="inline-block min-w-full align-middle">
                      <table className="min-w-full">
                        <thead>
                          <tr className="text-xs font-medium text-gray-500 dark:text-slate-300 uppercase tracking-wider">
                            <th className="px-2 py-1 text-left">Author</th>
                            <th className="px-2 py-1 text-left">Date & Time</th>
                            <th className="px-2 py-1 text-left">Hours</th>
                            <th className="px-2 py-1 text-left">Description</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                          {group.logs.map((log, index) => (
                            <tr key={index} className="text-sm">
                              <td className="px-2 py-1 text-gray-900 dark:text-slate-100">{log.author}</td>
                              <td className="px-2 py-1 text-gray-600 dark:text-slate-400">
                                <div>{formatDateStable(log.started)}</div>
                                <div className="text-xs text-gray-500 dark:text-slate-500">{formatTimeStable(log.started)}</div>
                              </td>
                              <td className="px-2 py-1 text-gray-900 dark:text-slate-100 font-medium">{log.loggedHours}h</td>
                              <td className="px-2 py-1 text-gray-600 dark:text-slate-400 max-w-xs">
                                {log.comment ? (
                                  <div className="text-xs" title={log.comment}>
                                    {log.comment.length > 150 ? 
                                      `${log.comment.substring(0, 150)}...` : 
                                      log.comment
                                    }
                                  </div>
                                ) : (
                                  <span className="text-gray-400 dark:text-slate-500 text-xs">No description</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default IssueGroupedView;

